import React, { ReactElement } from 'react'

import ServiceLvlSettings from './ServiceLvlSettings'
import InboundStatSettings from './InboundStatSettings'
import AnswerPerformance from './AnswerPerformance'
import UnreturnedCalls from './UnreturnedCalls'
import TalkTime from './TalkTime'
import AbandonedTimeStatistic from './AbandonedTimeStatistic'
import UnreturnedCallsFiltering from './UnreturnedCallsFiltering'

// CSS imports. Commented out because of current build process.
// import './SLAKPITab.scss'

/**
 * Component properties type.
 */
type Props = {
    callsAnsWithin: number | undefined
    setCallsAnsWithin: (newValue: number | undefined) => void
    isSrvLvlIgnQuickAbndCallsWithinEnabled: boolean
    setSrvLvlIgnQuickAbndCallsWithinEnabled: (checked: boolean) => void
    srvLvlIgnQuickAbndCallsWithin: number | undefined
    setSrvLvlIgnQuickAbndCallsWithin: (newValue: number | undefined) => void
    isSrvLvlIgnOvrflwdCallsWithinEnabled: boolean
    setSrvLvlIgnOvrflwdCallsWithinEnabled: (checked: boolean) => void
    srvLvlIgnOvrflwdCallsWithin: number | undefined
    setSrvLvlIgnOvrflwdCallsWithin: (newValue: number | undefined) => void
    isInbStIgnQuickAbndCallsWithinEnabled: boolean
    setInbStIgnQuickAbndCallsWithinEnabled: (checked: boolean) => void
    inbStIgnQuickAbndCallsWithin: number | undefined
    setInbStIgnQuickAbndCallsWithin: (newValue: number | undefined) => void
    isInbStIgnOvrflwdCallsWithinEnabled: boolean
    setInbStIgnOvrflwdCallsWithinEnabled: (checked: boolean) => void
    inbStIgnOvrflwdCallsWithin: number | undefined
    setInbStIgnOvrflwdCallsWithin: (newValue: number | undefined) => void
    callsInAnsThres1: number | undefined
    setCallsInAnsThres1: (newValue: number | undefined) => void
    callsInAnsThres2: number | undefined
    setCallsInAnsThres2: (newValue: number | undefined) => void
    callsInAnsThres3: number | undefined
    setCallsInAnsThres3: (newValue: number | undefined) => void
    callsInAnsThres4: number | undefined
    setCallsInAnsThres4: (newValue: number | undefined) => void
    callsInAnsThres5: number | undefined
    setCallsInAnsThres5: (newValue: number | undefined) => void
    callsInAnsThres6: number | undefined
    setCallsInAnsThres6: (newValue: number | undefined) => void
    resetAnswerTargets: () => void
    callsInAbndThres1: number | undefined
    setCallsInAbndThres1: (newValue: number | undefined) => void
    callsInAbndThres2: number | undefined
    setCallsInAbndThres2: (newValue: number | undefined) => void
    callsInAbndThres3: number | undefined
    setCallsInAbndThres3: (newValue: number | undefined) => void
    callsInAbndThres4: number | undefined
    setCallsInAbndThres4: (newValue: number | undefined) => void
    callsInAbndThres5: number | undefined
    setCallsInAbndThres5: (newValue: number | undefined) => void
    callsInAbndThres6: number | undefined
    setCallsInAbndThres6: (newValue: number | undefined) => void
    resetAbandonedTargets: () => void
    supportsCalculatedStatistics: boolean
    supportsInboundStatSettings: boolean
    supportsExclusionOfHeldTimeFromTalkTimeSettings: boolean
    includeEndedOnHold: boolean
    setIncludeEndedOnHold: (checked: boolean) => void
    includeEscaped: boolean
    setIncludeEscaped: (checked: boolean) => void
    excludeHeldTime: boolean
    setExcludeHeldTime: (checked: boolean) => void
    isInboundEscapedCallsIgnored: boolean
    setIsInboundEscapedCallsIgnored: (checked: boolean) => void
    includeEscapedToCallsFilter: boolean
    setIncludeEscapedToCallsFilter: (checked: boolean) => void
    includeDivertedOverflowedToCallsFilter: boolean
    setIncludeDivertedOverflowedToCallsFiltered: (checked: boolean) => void
    includeTransferredToCallsFilter: boolean
    setIncludeTransferredToCallsFilter: (checked: boolean) => void
    includeAllOtherCallsFilter: boolean
    setIncludeAllOtherCallsFilter: (checked: boolean) => void
}

/**
 * SLA/KPI tab in report configuration modal window.
 *
 * @param callsAnsWithin Report property.
 * @param setCallsAnsWithin Setter for corresponding report property.
 * @param isSrvLvlIgnQuickAbndCallsWithinEnabled Whether the input for corresponding property is enabled.
 * @param setSrvLvlIgnQuickAbndCallsWithinEnabled Setter to enable corresponding property input.
 * @param srvLvlIgnQuickAbndCallsWithin Report property.
 * @param setSrvLvlIgnQuickAbndCallsWithin Setter for corresponding report property.
 * @param isSrvLvlIgnOvrflwdCallsWithinEnabled Whether the input for corresponding property is enabled.
 * @param setSrvLvlIgnOvrflwdCallsWithinEnabled Setter to enable corresponding property input.
 * @param srvLvlIgnOvrflwdCallsWithin Report property.
 * @param setSrvLvlIgnOvrflwdCallsWithin Setter for corresponding report property.
 * @param isInbStIgnQuickAbndCallsWithinEnabled Whether the input for corresponding property is enabled.
 * @param setInbStIgnQuickAbndCallsWithinEnabled Setter to enable corresponding property input.
 * @param inbStIgnQuickAbndCallsWithin Report property.
 * @param setInbStIgnQuickAbndCallsWithin Setter for corresponding report property.
 * @param isInbStIgnOvrflwdCallsWithinEnabled Whether the input for corresponding property is enabled.
 * @param setInbStIgnOvrflwdCallsWithinEnabled Setter to enable corresponding property input.
 * @param inbStIgnOvrflwdCallsWithin Report property.
 * @param setInbStIgnOvrflwdCallsWithin Setter for corresponding report property.
 * @param callsInAnsThres1 Report property.
 * @param setCallsInAnsThres1 Setter for corresponding report property.
 * @param callsInAnsThres2 Report property.
 * @param setCallsInAnsThres2 Setter for corresponding report property.
 * @param callsInAnsThres3 Report property.
 * @param setCallsInAnsThres3 Setter for corresponding report property.
 * @param callsInAnsThres4 Report property.
 * @param setCallsInAnsThres4 Setter for corresponding report property.
 * @param callsInAnsThres5 Report property.
 * @param setCallsInAnsThres5 Setter for corresponding report property.
 * @param callsInAnsThres6 Report property.
 * @param setCallsInAnsThres6 Setter for corresponding report property.
 * @param resetAnswerTargets Resets answer targets.
 * @param callsInAbndThres1 Report property.
 * @param setCallsInAbndThres1 Setter for corresponding report property.
 * @param callsInAbndThres2 Report property.
 * @param setCallsInAbndThres2 Setter for corresponding report property.
 * @param callsInAbndThres3 Report property.
 * @param setCallsInAbndThres3 Setter for corresponding report property.
 * @param callsInAbndThres4 Report property.
 * @param setCallsInAbndThres4 Setter for corresponding report property.
 * @param callsInAbndThres5 Report property.
 * @param setCallsInAbndThres5 Setter for corresponding report property.
 * @param callsInAbndThres6 Report property.
 * @param setCallsInAbndThres6 Setter for corresponding report property.
 * @param resetAbandonedTargets Resets abandoned targets.
 * @param supportsCalculatedStatistics Whether the calculated statistics are supported.
 * @param supportsInboundStatSettings Whether the inbound statistics are supported.
 * @param supportsExclusionOfHeldTimeFromTalkTimeSettings Whether the exclusion of held time setting is supported.
 * @param includeEndedOnHold Report property.
 * @param setIncludeEndedOnHold Setter for corresponding report property.
 * @param includeEscaped Report property.
 * @param setIncludeEscaped Setter for corresponding report property.
 * @param excludeHeldTime Report property.
 * @param setExcludeHeldTime Setter for corresponding report property.
 * @param isInboundEscapedCallsIgnored Report property.
 * @param setIsInboundEscapedCallsIgnored Setter for corresponding report property.
 * @param includeEscapedToCallsFilter Report property.
 * @param setIncludeEscapedToCallsFilter Setter for corresponding report property.
 * @param includeDivertedOverflowedToCallsFilter Report property.
 * @param setIncludeDivertedOverflowedToCallsFiltered Setter for corresponding report property.
 * @param includeTransferredToCallsFilter Report property.
 * @param setIncludeTransferredToCallsFilter Setter for corresponding report property.
 * @param includeAllOtherCallsFilter Report property.
 * @param setIncludeAllOtherCallsFilter Setter for corresponding report property.
 */
const SLAKPITab: React.FC<Props> = ({
    callsAnsWithin,
    setCallsAnsWithin,
    isSrvLvlIgnQuickAbndCallsWithinEnabled,
    setSrvLvlIgnQuickAbndCallsWithinEnabled,
    srvLvlIgnQuickAbndCallsWithin,
    setSrvLvlIgnQuickAbndCallsWithin,
    isSrvLvlIgnOvrflwdCallsWithinEnabled,
    setSrvLvlIgnOvrflwdCallsWithinEnabled,
    srvLvlIgnOvrflwdCallsWithin,
    setSrvLvlIgnOvrflwdCallsWithin,
    isInbStIgnQuickAbndCallsWithinEnabled,
    setInbStIgnQuickAbndCallsWithinEnabled,
    inbStIgnQuickAbndCallsWithin,
    setInbStIgnQuickAbndCallsWithin,
    isInbStIgnOvrflwdCallsWithinEnabled,
    setInbStIgnOvrflwdCallsWithinEnabled,
    inbStIgnOvrflwdCallsWithin,
    setInbStIgnOvrflwdCallsWithin,
    callsInAnsThres1,
    setCallsInAnsThres1,
    callsInAnsThres2,
    setCallsInAnsThres2,
    callsInAnsThres3,
    setCallsInAnsThres3,
    callsInAnsThres4,
    setCallsInAnsThres4,
    callsInAnsThres5,
    setCallsInAnsThres5,
    callsInAnsThres6,
    setCallsInAnsThres6,
    resetAnswerTargets,
    callsInAbndThres1,
    setCallsInAbndThres1,
    callsInAbndThres2,
    setCallsInAbndThres2,
    callsInAbndThres3,
    setCallsInAbndThres3,
    callsInAbndThres4,
    setCallsInAbndThres4,
    callsInAbndThres5,
    setCallsInAbndThres5,
    callsInAbndThres6,
    setCallsInAbndThres6,
    resetAbandonedTargets,
    supportsCalculatedStatistics,
    supportsInboundStatSettings,
    supportsExclusionOfHeldTimeFromTalkTimeSettings,
    includeEndedOnHold,
    setIncludeEndedOnHold,
    includeEscaped,
    setIncludeEscaped,
    excludeHeldTime,
    setExcludeHeldTime,
    isInboundEscapedCallsIgnored,
    setIsInboundEscapedCallsIgnored,
    includeEscapedToCallsFilter,
    setIncludeEscapedToCallsFilter,
    includeDivertedOverflowedToCallsFilter,
    setIncludeDivertedOverflowedToCallsFiltered,
    includeTransferredToCallsFilter,
    setIncludeTransferredToCallsFilter,
    includeAllOtherCallsFilter,
    setIncludeAllOtherCallsFilter
}: Props): ReactElement => (
    <div className='sla-kpi-tab tab-area'>
        {supportsCalculatedStatistics && (
            <ServiceLvlSettings
                callsAnsWithin={callsAnsWithin}
                setCallsAnsWithin={setCallsAnsWithin}
                isSrvLvlIgnQuickAbndCallsWithinEnabled={isSrvLvlIgnQuickAbndCallsWithinEnabled}
                setSrvLvlIgnQuickAbndCallsWithinEnabled={setSrvLvlIgnQuickAbndCallsWithinEnabled}
                srvLvlIgnQuickAbndCallsWithin={srvLvlIgnQuickAbndCallsWithin}
                setSrvLvlIgnQuickAbndCallsWithin={setSrvLvlIgnQuickAbndCallsWithin}
                isSrvLvlIgnOvrflwdCallsWithinEnabled={isSrvLvlIgnOvrflwdCallsWithinEnabled}
                setSrvLvlIgnOvrflwdCallsWithinEnabled={setSrvLvlIgnOvrflwdCallsWithinEnabled}
                srvLvlIgnOvrflwdCallsWithin={srvLvlIgnOvrflwdCallsWithin}
                setSrvLvlIgnOvrflwdCallsWithin={setSrvLvlIgnOvrflwdCallsWithin}
            />
        )}
        {(supportsInboundStatSettings || supportsCalculatedStatistics) && (
            <>
                <UnreturnedCalls
                    includeEndedOnHold={includeEndedOnHold}
                    setIncludeEndedOnHold={setIncludeEndedOnHold}
                    includeEscaped={includeEscaped}
                    setIncludeEscaped={setIncludeEscaped}
                />
                <UnreturnedCallsFiltering
                    includeEscapedToCallsFilter={includeEscapedToCallsFilter}
                    setIncludeEscapedToCallsFilter={setIncludeEscapedToCallsFilter}
                    includeDivertedOverflowedToCallsFilter={includeDivertedOverflowedToCallsFilter}
                    setIncludeDivertedOverflowedToCallsFiltered={setIncludeDivertedOverflowedToCallsFiltered}
                    includeTransferredToCallsFilter={includeTransferredToCallsFilter}
                    setIncludeTransferredToCallsFilter={setIncludeTransferredToCallsFilter}
                    includeAllOtherCallsFilter={includeAllOtherCallsFilter}
                    setIncludeAllOtherCallsFilter={setIncludeAllOtherCallsFilter}
                />
            </>
        )}
        {supportsExclusionOfHeldTimeFromTalkTimeSettings && (
            <TalkTime excludeHeldTime={excludeHeldTime} setExcludeHeldTime={setExcludeHeldTime} />
        )}

        <InboundStatSettings
            isInbStIgnQuickAbndCallsWithinEnabled={isInbStIgnQuickAbndCallsWithinEnabled}
            setInbStIgnQuickAbndCallsWithinEnabled={setInbStIgnQuickAbndCallsWithinEnabled}
            inbStIgnQuickAbndCallsWithin={inbStIgnQuickAbndCallsWithin}
            setInbStIgnQuickAbndCallsWithin={setInbStIgnQuickAbndCallsWithin}
            isInbStIgnOvrflwdCallsWithinEnabled={isInbStIgnOvrflwdCallsWithinEnabled}
            setInbStIgnOvrflwdCallsWithinEnabled={setInbStIgnOvrflwdCallsWithinEnabled}
            inbStIgnOvrflwdCallsWithin={inbStIgnOvrflwdCallsWithin}
            setInbStIgnOvrflwdCallsWithin={setInbStIgnOvrflwdCallsWithin}
            isInboundEscapedCallsIgnored={isInboundEscapedCallsIgnored}
            setIsInboundEscapedCallsIgnored={setIsInboundEscapedCallsIgnored}
        />
        {supportsCalculatedStatistics && (
            <>
                <AnswerPerformance
                    callsInAnsThres1={callsInAnsThres1}
                    setCallsInAnsThres1={setCallsInAnsThres1}
                    callsInAnsThres2={callsInAnsThres2}
                    setCallsInAnsThres2={setCallsInAnsThres2}
                    callsInAnsThres3={callsInAnsThres3}
                    setCallsInAnsThres3={setCallsInAnsThres3}
                    callsInAnsThres4={callsInAnsThres4}
                    setCallsInAnsThres4={setCallsInAnsThres4}
                    callsInAnsThres5={callsInAnsThres5}
                    setCallsInAnsThres5={setCallsInAnsThres5}
                    callsInAnsThres6={callsInAnsThres6}
                    setCallsInAnsThres6={setCallsInAnsThres6}
                    resetAnswerTargets={resetAnswerTargets}
                />
                <AbandonedTimeStatistic
                    callsInAbndThres1={callsInAbndThres1}
                    setCallsInAbndThres1={setCallsInAbndThres1}
                    callsInAbndThres2={callsInAbndThres2}
                    setCallsInAbndThres2={setCallsInAbndThres2}
                    callsInAbndThres3={callsInAbndThres3}
                    setCallsInAbndThres3={setCallsInAbndThres3}
                    callsInAbndThres4={callsInAbndThres4}
                    setCallsInAbndThres4={setCallsInAbndThres4}
                    callsInAbndThres5={callsInAbndThres5}
                    setCallsInAbndThres5={setCallsInAbndThres5}
                    callsInAbndThres6={callsInAbndThres6}
                    setCallsInAbndThres6={setCallsInAbndThres6}
                    resetAbandonedTargets={resetAbandonedTargets}
                />
            </>
        )}
    </div>
)

export default SLAKPITab
